import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Button, Modal } from "react-bootstrap";

import { catalog, estimateOrder, mine as myProducts } from "../../../actions/products";
import {
    addProduct,
    applyFreeLicenseLimits,
    hydratePlanSelections,
    initialProduct,
    initialStep,
    normalizeCatalog,
    orderSelections,
    planErrors,
    removeProduct,
    validateStepOne,
    validateStepTwo
} from "./state";
import { productRecommendations } from "./recommendations";
import {
    LoadingLabel,
    PlanSelectionStep,
    ProductSelectionStep,
    ReviewOrderStep,
    WizardProgress
} from "./components";
import "./wizard.css";

const emptyState = props => ({
    currentStep: initialStep(props),
    selectedProductIds: [],
    selectedPlanByProductId: {},
    productCatalog: [],
    plansByProductId: {},
    includedProductIds: [],
    estimate: null,
    initialSelectionSignature: "",
    loading: {
        products: false,
        plans: false,
        estimate: false,
        submit: false
    },
    errors: {
        products: null,
        plans: {},
        estimate: null,
        submit: null
    }
});

export class ProductOrderWizard extends React.Component {
    constructor(props) {
        super(props);
        this.state = emptyState(props);
        this.stepHeadingTimer = null;
    }

    componentDidMount() {
        if (this.props.show || this.props.open) this.fetchData();
    }

    componentDidUpdate(previousProps) {
        const wasOpen = Boolean(previousProps.show || previousProps.open);
        const isOpen = Boolean(this.props.show || this.props.open);
        const entryChanged = (
            previousProps.category !== this.props.category
            || previousProps.initialProductId !== this.props.initialProductId
            || previousProps.initialProductCategoryId !== this.props.initialProductCategoryId
            || previousProps.existingLicenseId !== this.props.existingLicenseId
            || previousProps.selected !== this.props.selected
        );
        if (isOpen && (!wasOpen || entryChanged)) {
            this.setState(emptyState(this.props), this.fetchData);
        }
    }

    componentWillUnmount() {
        if (this.stepHeadingTimer) window.clearTimeout(this.stepHeadingTimer);
    }

    get isOpen() {
        return Boolean(this.props.show || this.props.open);
    }

    get currentPlanId() {
        return this.props.currentPlanId || this.props.selected || null;
    }

    get existingLicenseId() {
        return this.props.existingLicenseId || this.props.licenseId || null;
    }

    get restrictToInitialProduct() {
        return Boolean(this.props.restrictToInitialProduct);
    }

    get selectionSignature() {
        return JSON.stringify({
            products: this.state.selectedProductIds,
            plans: this.state.selectedPlanByProductId
        });
    }

    get hasUnsavedChanges() {
        return Boolean(
            this.state.initialSelectionSignature
            && this.selectionSignature !== this.state.initialSelectionSignature
        );
    }

    fetchData = async () => {
        if (!this.isOpen) return;
        this.setState(previous => ({
            loading: { ...previous.loading, products: true },
            errors: { ...previous.errors, products: null }
        }));

        const [catalogResponse, ownedResponse] = await Promise.all([
            this.props.catalog(),
            this.fetchLicenseInventory()
        ]);
        if (!this.isOpen) return;

        const owned = Array.isArray(ownedResponse) ? ownedResponse : [];
        const products = applyFreeLicenseLimits(normalizeCatalog(catalogResponse), owned, {
            existingLicenseId: this.existingLicenseId,
            licenseeUuid: this.props.auth?.user?.licenseeUuid
        });
        if (catalogResponse?.success === false || products.length === 0) {
            this.setState(previous => ({
                loading: { ...previous.loading, products: false },
                errors: {
                    ...previous.errors,
                    products: catalogResponse?.error?.message || catalogResponse?.error || "Products could not be loaded. Try again."
                }
            }));
            return;
        }

        const requestedProduct = initialProduct(this.props);
        const selectedProductIds = requestedProduct && products.some(product => product.id === requestedProduct)
            ? [requestedProduct]
            : [];
        const includedProductIds = [...new Set(owned
            .map(item => item?.Product?.category || item?.category)
            .filter(Boolean))];
        const selectedPlanByProductId = hydratePlanSelections({
            products,
            selectedProductIds,
            previous: {},
            currentPlanId: this.currentPlanId
        });
        const next = {
            currentStep: selectedProductIds.length > 0 ? initialStep(this.props) : 1,
            selectedProductIds,
            selectedPlanByProductId,
            productCatalog: products,
            plansByProductId: Object.fromEntries(products.map(product => [product.id, product.plans])),
            includedProductIds,
            estimate: null,
            loading: { products: false, plans: false, estimate: false, submit: false },
            errors: { products: null, plans: {}, estimate: null, submit: null }
        };
        next.initialSelectionSignature = JSON.stringify({
            products: next.selectedProductIds,
            plans: next.selectedPlanByProductId
        });
        this.setState(next, this.focusStepHeading);
    }

    fetchLicenseInventory = async () => {
        const licenses = [];
        let page = 0;
        while (true) {
            const rows = await this.props.myProducts({ category: "ALL", page, size: 100 });
            if (!Array.isArray(rows)) break;
            licenses.push(...rows);
            if (rows.length < 100 || licenses.length >= Number(rows[0]?.count)) break;
            page += 1;
        }
        return licenses;
    }

    focusStepHeading = () => {
        if (this.stepHeadingTimer) window.clearTimeout(this.stepHeadingTimer);
        this.stepHeadingTimer = window.setTimeout(() => {
            document.getElementById("product-order-step-heading")?.focus();
        }, 0);
    }

    includedForSelection = productId => (
        this.state.includedProductIds.includes(productId)
        && !this.state.selectedProductIds.includes(productId)
    );

    onToggleProduct = productId => {
        if (this.includedForSelection(productId) || this.state.loading.submit) return;
        if (this.state.selectedProductIds.includes(productId)) {
            if (this.restrictToInitialProduct && productId === initialProduct(this.props)) return;
            const next = removeProduct(this.state, productId);
            if (next.selectedProductIds.length === 0 && this.state.currentStep > 1) next.currentStep = 1;
            this.setState(next);
            return;
        }

        this.setState(previous => {
            const selectedProductIds = addProduct(previous.selectedProductIds, productId);
            return {
                selectedProductIds,
                selectedPlanByProductId: hydratePlanSelections({
                    products: previous.productCatalog,
                    selectedProductIds,
                    previous: previous.selectedPlanByProductId,
                    currentPlanId: this.currentPlanId
                }),
                estimate: null,
                errors: { ...previous.errors, products: null, estimate: null }
            };
        });
    }

    onRemoveProduct = productId => {
        if (this.restrictToInitialProduct && productId === initialProduct(this.props)) return;
        const next = removeProduct(this.state, productId);
        if (next.selectedProductIds.length === 0) next.currentStep = 1;
        this.setState(next, this.focusStepHeading);
    }

    onPlan = (productId, planId) => {
        const plan = this.state.productCatalog.find(product => product.id === productId)?.plans.find(item => item.id === planId);
        if (!plan || plan.disabled || plan.custom) return;
        this.setState(previous => {
            const errors = { ...previous.errors, plans: { ...previous.errors.plans }, estimate: null };
            delete errors.plans[productId];
            return {
                selectedPlanByProductId: {
                    ...previous.selectedPlanByProductId,
                    [productId]: planId
                },
                estimate: null,
                errors
            };
        });
    }

    canVisitStep = step => {
        if (this.state.loading.submit) return false;
        if (step === 1) return !this.restrictToInitialProduct;
        if (step === 2) return validateStepOne(this.state.selectedProductIds);
        return validateStepTwo(this.state) && Boolean(this.state.estimate);
    }

    onProgressStep = step => {
        if (step === this.state.currentStep || !this.canVisitStep(step)) return;
        this.setState({ currentStep: step }, this.focusStepHeading);
    }

    onNextFromProducts = () => {
        if (!validateStepOne(this.state.selectedProductIds)) {
            this.setState(previous => ({ errors: { ...previous.errors, products: "Choose at least one product." } }));
            return;
        }
        this.setState(previous => ({
            currentStep: 2,
            selectedPlanByProductId: hydratePlanSelections({
                products: previous.productCatalog,
                selectedProductIds: previous.selectedProductIds,
                previous: previous.selectedPlanByProductId,
                currentPlanId: this.currentPlanId
            }),
            errors: { ...previous.errors, products: null }
        }), this.focusStepHeading);
    }

    onEstimate = async () => {
        const errors = planErrors(this.state);
        if (Object.keys(errors).length > 0) {
            this.setState(previous => ({ errors: { ...previous.errors, plans: errors } }), () => {
                document.getElementById(`product-${Object.keys(errors)[0]}-plans`)?.focus();
            });
            return;
        }

        this.setState(previous => ({
            loading: { ...previous.loading, estimate: true },
            errors: { ...previous.errors, estimate: null, plans: {} }
        }));
        const response = await this.props.estimateOrder({
            selections: orderSelections(this.state),
            existingLicenseId: this.existingLicenseId
        });
        if (response?.success && response?.estimate?.serverCalculated) {
            this.setState(previous => ({
                currentStep: 3,
                estimate: response.estimate,
                loading: { ...previous.loading, estimate: false }
            }), this.focusStepHeading);
            return;
        }

        this.setState(previous => ({
            loading: { ...previous.loading, estimate: false },
            errors: {
                ...previous.errors,
                estimate: response?.error?.message || response?.error || "We could not calculate the order total."
            }
        }));
    }

    onSubmit = async () => {
        if (this.state.loading.submit || !this.state.estimate) return;
        if (!validateStepTwo(this.state)) return;
        this.setState(previous => ({
            loading: { ...previous.loading, submit: true },
            errors: { ...previous.errors, submit: null }
        }));
        try {
            const result = await this.props.onOk({
                selections: orderSelections(this.state),
                estimate: this.state.estimate,
                existingLicenseId: this.existingLicenseId
            });
            if (result?.success === false) {
                this.setState(previous => ({
                    loading: { ...previous.loading, submit: false },
                    errors: {
                        ...previous.errors,
                        submit: result?.error?.message || result?.error || "The order was not completed. No charge was made."
                    }
                }));
            }
        } catch (error) {
            this.setState(previous => ({
                loading: { ...previous.loading, submit: false },
                errors: { ...previous.errors, submit: error?.message || "The order was not completed. No charge was made." }
            }));
        }
    }

    onCancel = event => {
        if (this.state.loading.submit) return;
        if (this.hasUnsavedChanges && typeof window.confirm === "function") {
            const confirmed = window.confirm("Discard the product and plan selections in this order?");
            if (!confirmed) return;
        }
        this.props.onCancel(event);
    }

    renderBody() {
        const recommendations = productRecommendations({
            products: this.state.productCatalog,
            selectedProductIds: this.state.selectedProductIds,
            ownedProductIds: this.state.includedProductIds
        });
        if (this.state.currentStep === 1) {
            return (
                <ProductSelectionStep
                    products={this.state.productCatalog}
                    selectedProductIds={this.state.selectedProductIds}
                    includedProductIds={this.state.includedProductIds.filter(this.includedForSelection)}
                    loading={this.state.loading.products}
                    error={this.state.errors.products}
                    recommendations={recommendations}
                    onToggle={this.onToggleProduct}
                    onRetry={this.fetchData}
                />
            );
        }
        if (this.state.currentStep === 2) {
            return (
                <PlanSelectionStep
                    products={this.state.productCatalog}
                    selectedProductIds={this.state.selectedProductIds}
                    selectedPlanByProductId={this.state.selectedPlanByProductId}
                    currentPlanId={this.currentPlanId}
                    planErrors={this.state.errors.plans}
                    restrictToInitialProduct={this.restrictToInitialProduct}
                    onPlan={this.onPlan}
                    onRemove={this.onRemoveProduct}
                />
            );
        }
        return <ReviewOrderStep estimate={this.state.estimate} existingLicenseId={this.existingLicenseId} />;
    }

    renderFooter() {
        const step = this.state.currentStep;
        const busy = this.state.loading.submit;
        const dueToday = Number(this.state.estimate?.dueToday || 0);
        const submitLabel = this.existingLicenseId
            ? "Confirm Change"
            : dueToday > 0
                ? `Pay ${(dueToday / 100).toLocaleString("en-US", { style: "currency", currency: "USD" })} Today`
                : "Confirm Order";
        return (
            <div className="product-order-footer-actions">
                {step === 1 ? (
                    <Button variant="outline-secondary" onClick={this.onCancel}>Cancel</Button>
                ) : (
                    <Button
                        variant="outline-secondary"
                        disabled={busy || (step === 2 && this.restrictToInitialProduct)}
                        onClick={() => this.onProgressStep(step - 1)}
                    >← Back</Button>
                )}
                {step === 1 ? (
                    <Button variant="primary" disabled={!validateStepOne(this.state.selectedProductIds)} onClick={this.onNextFromProducts}>Next →</Button>
                ) : step === 2 ? (
                    <Button variant="primary" disabled={this.state.loading.estimate || !validateStepTwo(this.state)} onClick={this.onEstimate}>
                        {this.state.loading.estimate ? <LoadingLabel>Calculating…</LoadingLabel> : <>Next →</>}
                    </Button>
                ) : (
                    <Button variant="primary" disabled={busy || !this.state.estimate} onClick={this.onSubmit}>
                        {busy ? <LoadingLabel>Submitting…</LoadingLabel> : submitLabel}
                    </Button>
                )}
            </div>
        );
    }

    render() {
        if (!this.isOpen) return null;
        return (
            <Modal
                dialogClassName="product-order-dialog"
                contentClassName="product-order-modal"
                backdrop="static"
                keyboard={false}
                animation={false}
                show={this.isOpen}
                onHide={this.onCancel}
                aria-labelledby="product-order-title"
            >
                <Modal.Header closeButton={!this.state.loading.submit}>
                    <div>
                        <Modal.Title id="product-order-title">{this.existingLicenseId ? "Modify License" : "New License"}</Modal.Title>
                        <div className="text-muted text-small">Build your Feradel product order</div>
                    </div>
                </Modal.Header>
                <WizardProgress currentStep={this.state.currentStep} canVisit={this.canVisitStep} onStep={this.onProgressStep} />
                <Modal.Body>
                    {this.state.errors.estimate ? <div className="alert alert-danger" role="alert">{this.state.errors.estimate}</div> : null}
                    {this.state.errors.submit ? <div className="alert alert-danger" role="alert">{this.state.errors.submit}</div> : null}
                    {this.renderBody()}
                </Modal.Body>
                <Modal.Footer>{this.renderFooter()}</Modal.Footer>
            </Modal>
        );
    }
}

ProductOrderWizard.propTypes = {
    auth: PropTypes.object.isRequired,
    catalog: PropTypes.func.isRequired,
    estimateOrder: PropTypes.func.isRequired,
    myProducts: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onOk: PropTypes.func.isRequired,
    restrictToInitialProduct: PropTypes.bool
};

ProductOrderWizard.defaultProps = {
    restrictToInitialProduct: false
};

const mapStateToProps = state => ({ auth: state.auth });

export default connect(mapStateToProps, { catalog, estimateOrder, myProducts })(ProductOrderWizard);
